import React from 'react';
import styled from 'styled-components';
import { ReactComponent as OvernightIcon } from '@/assets/images/order/overnightIcon.svg';
import { ReactComponent as DawnIcon } from '@/assets/images/order/dawnIcon.svg';
import { ReactComponent as ShoppingBag } from '@/assets/images/order/shoppingBag.svg';
import { ReactComponent as FileRemove } from '@/assets/images/common/FileRemove.svg';
import { Order } from '@/apis/order/interface';
import theme from '@/styles/theme';
import TodayIcon from '@/components/icon/order/TodayIcon';
import DeliveryIcon from '@/components/icon/order/DeliveryIcon';
import PostIcon from '@/components/icon/order/PostIcon';
import PickupIcon from '@/components/icon/order/PickupIcon';
import { OrderStatusType } from '@/@types/order';

export const getOrderIcon = (orderType: string) => {
  switch (orderType) {
    case 'delivery':
      return <TodayIcon />;
    case 'quick':
      return <DeliveryIcon />;
    case 'post':
      return <PostIcon />;
    case 'partnership_pickup':
      return <PickupIcon />;
    case 'overnight':
      return <OvernightIcon />;
    case 'dawn':
      return <DawnIcon />;
  }
};

export const getOrderLabel = (orderType: string, isShort?: boolean) => {
  switch (orderType) {
    case 'delivery':
      return isShort ? '오늘' : '오늘';
    case 'quick':
      return isShort ? '퀵' : '퀵';
    case 'post':
      return isShort ? '택배' : '택배';
    case 'partnership_pickup':
      return isShort ? '방문' : '방문수령';
    case 'overnight':
      return isShort ? '익일' : '익일';
    case 'dawn':
      return isShort ? '새벽' : '새벽';
  }
};
export const formatToIconOrderType = (
  orderType: string,
  agencyType?: string
) => {
  const icon = getOrderIcon(orderType);
  const label = getOrderLabel(orderType, true);
  return (
    <OrderType>
      {icon}
      {label}
    </OrderType>
  );
};

export const getRowBgColor = (order: Order) => {
  if (order.isPrescriptionRevised) {
    return theme.DF_COLOR.RED200;
  }
  return theme.DF_COLOR.WHITE;
};

const OrderType = styled.div`
  display: inline-flex;
  align-items: center;
  ${({ theme }) => theme.DF_TYPOGRAPHY.UI16M};
  color: ${({ theme }) => theme.DF_COLOR.GRAY900};

  > * {
    &:first-child {
      margin-right: 8px;
    }
  }
`;

type ProcessStatusParams = Pick<
  Order,
  | 'orderStatus'
  | 'payStatus'
  | 'callAt'
  | 'deliveryAt'
  | 'priceSetAt'
  | 'orderType'
  | 'isImmediatePayment'
>;
export const processStatus = (
  order: ProcessStatusParams,
  location?: string
) => {
  const {
    orderStatus,
    payStatus,
    callAt,
    deliveryAt,
    priceSetAt,
    orderType,
    isImmediatePayment
  } = order;
  if (payStatus === 'done' && location === 'requested') {
    return OrderStatusType.RedirectPreparing; // 가격요청 탭에서 결제완료된건 조제진행으로 redirect
  }
  if (orderStatus === 'cancel' && !!deliveryAt) {
    return OrderStatusType.PickupCancel; // 배차요청 후 배차 취소
  }
  if (
    (payStatus === 'done' && !!deliveryAt) ||
    (payStatus === 'done' && orderStatus === 'done')
  ) {
    return OrderStatusType.PickupComplete; // 수거 완료
  }
  if (orderStatus === 'cancel' && payStatus !== 'cancel') {
    return OrderStatusType.RequestedCancel; // 유저 취소
  }
  if (orderStatus === 'cancel' && payStatus === 'cancel') {
    return OrderStatusType.Cancel; // 약사 취소
  }
  if (
    orderStatus === 'progress' &&
    !!priceSetAt &&
    ['none', 'ready'].includes(payStatus) // react-query invalidateQuery 후, 서버 이벤트 consume 타이밍 이슈로 none 포함 필요
  ) {
    return OrderStatusType.WaitingPayment; // 가격 요청 후 결제 대기
  }
  if (!!callAt) {
    if (orderType === 'partnership_pickup') {
      return OrderStatusType.PartnershipPickupCheck; // 픽업 요청
    }
    // TODO: 즉시결제 orderType 추가
    return OrderStatusType.PickupCheck; // 배차요청 후 기사 수거 대기
  }
  if (orderStatus === 'progress' && payStatus === 'done') {
    if (isImmediatePayment) {
      return OrderStatusType.ImmediateOrder; // 즉시결제
    }
    if (orderType === 'partnership_pickup') {
      return OrderStatusType.PartnershipPickupRequest; // 픽업 요청
    }
    return OrderStatusType.RequestPickup; // 배차요청
  }
  return OrderStatusType.Ready; // 금액 설정
};

export const orderTableStatus = (order: Order) => {
  const status = processStatus(order);

  switch (status) {
    case OrderStatusType.PickupCancel:
      return (
        <StatusText>
          <span className={'canceled'}>취소완료</span>
        </StatusText>
      );
    case OrderStatusType.PickupComplete:
      return <StatusText>수거완료</StatusText>;
    case OrderStatusType.RequestedCancel:
      return (
        <StatusText>
          <span className={'canceled'}>취소완료</span>
        </StatusText>
      );
    case OrderStatusType.Cancel:
      return <StatusText>취소완료</StatusText>;
    case OrderStatusType.WaitingPayment:
      return <StatusText>결제요청</StatusText>;
    case OrderStatusType.PickupCheck:
      return <StatusText>수거대기</StatusText>;
    case OrderStatusType.PartnershipPickupCheck:
      return <StatusText>수령대기</StatusText>;
    case OrderStatusType.RequestPickup:
    case OrderStatusType.PartnershipPickupRequest:
    case OrderStatusType.ImmediateOrder:
      return (
        <StatusText>
          <em>조제중</em>
        </StatusText>
      );
    case OrderStatusType.Ready:
      return (
        <StatusText>
          <em>가격요청</em>
        </StatusText>
      );
  }
};

export const formatAdditionalStatus = ({
  hasAdditionalOrder,
  isPrescriptionRevised
}: {
  hasAdditionalOrder?: boolean;
  isPrescriptionRevised?: boolean;
}) => {
  if (hasAdditionalOrder) {
    return (
      <Row>
        <ShoppingBag />
        <StatusText style={{ color: theme.DF_COLOR.PRIMARY700 }}>
          추가주문
        </StatusText>
      </Row>
    );
  }
  if (isPrescriptionRevised) {
    return (
      <Row>
        <FileRemove style={{ width: 16, height: 16, marginRight: 4 }} />
        <StatusText style={{ color: theme.DF_COLOR.RED700 }}>
          처방전수정
        </StatusText>
      </Row>
    );
  }
  return '';
};

const StatusText = styled.span`
  ${({ theme }) => theme.DF_TYPOGRAPHY.UI17M};
  color: ${({ theme }) => theme.DF_COLOR.GRAY900};

  em {
    ${({ theme }) => theme.DF_TYPOGRAPHY.UI17SB};
    color: ${({ theme }) => theme.DF_COLOR.BLUE700};
    font-style: normal;
    font-weight: 600;
  }

  .canceled {
    ${({ theme }) => theme.DF_TYPOGRAPHY.UI17M};
    color: ${({ theme }) => theme.DF_COLOR.GRAY200};
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ theme }) => theme.DF_TYPOGRAPHY.UI17M};
  color: ${({ theme }) => theme.DF_COLOR.GRAY900};
`;
